<script lang="ts" setup>
  import { useNotify } from '@/composition/useNotify'
  import { createElement } from '@/helper/element-factory'
  import { useActionBarStore } from '@/store/actionBar'
  import { useBuilderStore } from '@/store/builder'
  import { useProductListStore } from '@/store/productListStore'
  import {
    Direction,
    ElementDropContainer,
    ELEMENTS_LOOKUP,
    IElement,
    PAGE_SECTIONS,
    resolveDropTargetBlock,
  } from '@gohighlevel/ghl-proposals-common'
  import { onClickOutside } from '@vueuse/core'
  import { cloneDeep } from 'lodash'
  import { computed, onBeforeUnmount, onMounted, PropType, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import RenderFloatingComponents from '../common/RenderFloatingComponents.vue'
  import BlockElementsRenderer from './BlockElementsRenderer.vue'

  const props = defineProps({
    sectionId: {
      type: String,
      required: true,
    },
    parentPageId: {
      type: String,
      required: true,
    },
    elements: {
      type: Array as PropType<Array<IElement>>,
      required: true,
    },
    pageIndex: {
      type: Number,
      required: true,
    },
    margins: {
      type: Object as PropType<{ left: number; right: number }>,
      required: true,
    },
    marginTop: {
      type: String,
      required: true,
    },
    sectionType: {
      type: String,
      required: true,
    },
  })

  const store = useBuilderStore()
  const actionBarStore = useActionBarStore()
  const productListStore = useProductListStore()
  const { handleErrorMessage } = useNotify()
  const { t } = useI18n()
  const containerRefSection = ref<HTMLElement>()
  const snapContainerClass = computed(() => `snapContainer-${props.pageIndex}-section`)
  const boundContainerClass = computed(() => `boundContainer-${props.pageIndex}-section`)

  const childElementsWithDraggable = computed(() => {
    return props.elements.filter(
      child => child && child.component && child.component.isDraggable && child.type !== ELEMENTS_LOOKUP.PAGE_BREAK
    )
  })

  const childComponentsWithoutDraggable = computed(() => {
    return props.elements.filter(
      child => child && child.component && !child.component.isDraggable && child.type !== ELEMENTS_LOOKUP.PAGE_BREAK
    )
  })

  const backgroundSize = computed(() => {
    return '100 px'
  })

  const pageStyles = computed(() => {
    const page = store.pages[props.pageIndex]
    const section = page?.component?.options[sectionType.value]
    return {
      backgroundColor: section?.responsiveStyles?.large?.backgroundColor || null,
      backgroundImage: `url('${section?.component?.options?.src}')`,
      backgroundPosition: section?.responsiveStyles?.large?.backgroundPosition,
      backgroundRepeat: section?.responsiveStyles?.large?.backgroundRepeat,
      backgroundSize: backgroundSize.value,
      opacity: section?.responsiveStyles?.large?.opacity / 100 || 1,
    }
  })

  const sectionType = computed(() => props.sectionType)

  const isHeaderFooterSection = computed(() => {
    return props.sectionType === PAGE_SECTIONS.HEADERS || props.sectionType === PAGE_SECTIONS.FOOTERS
  })

  // Check if this section is currently activated (for draggability control)
  const isSectionActivated = computed(() => {
    if (props.sectionType === PAGE_SECTIONS.HEADERS) {
      return store.isHeaderSectionActive
    } else if (props.sectionType === PAGE_SECTIONS.FOOTERS) {
      return store.isFooterSectionActive
    }
    return true // For non-header/footer sections, always allow dragging
  })

  const shouldShowActivated = computed(() => {
    return isSectionActivated.value
  })

  const shouldShowDragText = computed(() => {
    if (!isHeaderFooterSection.value) return false
    if (!shouldShowActivated.value) return false
    if (childComponentsWithoutDraggable.value.length > 0) return false
    if (childElementsWithDraggable.value.length > 0) return false
    return true
  })

  /**
   * Click-outside for empty header/footer sections only.
   * When the section has no elements, the click-outside listener inside ElementDropContainer will fail, so clicking outside the section does not deactivate. We therefore register our own
   * onClickOutside here on the section root (containerRefSection), which is always mounted and
   * stable. When the user adds an element, we stop this listener; BlockElementsRenderer's
   * ElementDropContainer then handles click-outside for the non-empty case.
   */
  const sectionClickOutsideStopRef = ref<(() => void) | undefined>(undefined)
  const CLICK_OUTSIDE_IGNORE_BASE = [
    '.overlay-actions',
    '.overlay-container',
    '.handle',
    '.v-binder-follower-container',
    '.moveable-wrapper--ables',
    '#active-elements-drawer',
    '#elements-drawer',
    '#btn_elements',
    '.n-modal-container',
    '#editor-toolbar',
    '.font-family-custom-dropdown',
    '#text-editor-toolbar-item-font-family',
  ]

  /** Header/footer wrappers sit outside `.page-section`; clicks there must not clear selection (e.g. double-click to open settings). */
  function clickOutsideIgnoreSelectors(): string[] {
    const extra =
      props.sectionType === PAGE_SECTIONS.HEADERS
        ? ['.page-header', '.page-header-content']
        : props.sectionType === PAGE_SECTIONS.FOOTERS
          ? ['.page-footer', '.page-footer-content']
          : []
    return [...CLICK_OUTSIDE_IGNORE_BASE, ...extra]
  }

  function startSectionClickOutside() {
    sectionClickOutsideStopRef.value?.()
    sectionClickOutsideStopRef.value = undefined
    if (!containerRefSection.value || !isHeaderFooterSection.value || childComponentsWithoutDraggable.value.length > 0) return
    const stop = onClickOutside(
      containerRefSection,
      () => {
        store.updateActiveElement(null)
      },
      { capture: false, ignore: clickOutsideIgnoreSelectors() }
    )
    sectionClickOutsideStopRef.value = stop
  }

  function stopSectionClickOutside() {
    sectionClickOutsideStopRef.value?.()
    sectionClickOutsideStopRef.value = undefined
  }

  onMounted(() => {
    if (isHeaderFooterSection.value && childComponentsWithoutDraggable.value.length === 0) {
      startSectionClickOutside()
    }
  })

  onBeforeUnmount(() => {
    stopSectionClickOutside()
  })

  watch(
    () => childComponentsWithoutDraggable.value.length,
    (len, prevLen) => {
      if (!isHeaderFooterSection.value) return
      if (len === 0) startSectionClickOutside()
      else if (prevLen === 0 && len > 0) stopSectionClickOutside()
    }
  )

  function onStart(_ev: MouseEvent, element: IElement) {
    store.setActiveElementId(element.id)
    store.moveElementDragState = true
  }

  function onEnd() {
    store.moveElementDragState = false
  }

  function moveElement(newRowIndex: number, oldRowIndex: number, element: IElement) {
    store.moveElementInSection(cloneDeep(element), props.sectionId, newRowIndex, oldRowIndex, sectionType.value)
  }

  function mouseEnter(e: any, element?: IElement) {
    e.preventDefault()
    store.changeHoverState(true)
    const section = e.target.closest('section')
    section.classList.add('el-active')
    store.updateFocusedElement(element)
  }

  function mouseLeave(e: any) {
    e.preventDefault()
    store.changeHoverState(false)
    const section = e.target.closest('section')
    section.classList.remove('el-active')
    store.updateFocusedElement()
  }

  function updateActiveElement(event: MouseEvent, element: IElement | null) {
    event.stopImmediatePropagation()

    // If clicking on elements drawer or the add element button and a header/footer section is active, don't deactivate
    if (!element) {
      const elementsDrawer = document.querySelector('#elements-drawer')
      const addElementButton = document.querySelector('#btn_elements')
      const activeElement = store.activeElement
      const isClickingElementsDrawer = elementsDrawer && elementsDrawer.contains(event.target as Node)
      const isClickingAddButton = addElementButton && (addElementButton.contains(event.target as Node) || addElementButton === event.target)
      if (isClickingElementsDrawer || isClickingAddButton) {
        if (activeElement) {
          // Check if active element IS a header/footer section
          const isSection = activeElement.type === ELEMENTS_LOOKUP.PAGE_SECTION
          // Check if active element is WITHIN a header/footer section
          const elementSection = store.getElementSection(activeElement.id)
          const isInHeaderOrFooter = elementSection === PAGE_SECTIONS.HEADERS || elementSection === PAGE_SECTIONS.FOOTERS
          if (isSection || isInHeaderOrFooter) {
            return
          }
        }
      }
    }

    store.updateActiveElement(element)
    productListStore.setActiveRow(null, null)
    store.moveElementDragState = false
    actionBarStore.dragStateIsActivated = false
    actionBarStore.setFloatingElementDragging(false)
  }

  function onDropElement(
    ev: DragEvent,
    option: {
      rowIndex?: number
      dir: Direction
      colIndex?: number
      rowColIndex?: number
      currentPageId?: string
      rowId?: string
    }
  ) {
    // Only allow dropping when section is activated (for header/footer sections)
    if (isHeaderFooterSection.value && !isSectionActivated.value) {
      ev.preventDefault()
      return
    }

    ev.preventDefault()
    actionBarStore.toggleDragState(false)
    store.moveElementDragState = false
    const action = ev?.dataTransfer?.getData('action')
    if (action === 'add') {
      onDrop(ev, option.rowIndex as number, option.colIndex as number, option.rowColIndex as number)
    } else {
      const existingElement = actionBarStore.lastActiveDragElement
      if (existingElement) {
        const pageId = store.getPageIdBySectionElementId(existingElement?.id, sectionType.value)
        if (pageId) {
          // Element is already in a section, move it within the section
          const el = cloneDeep(existingElement)
          if (el) {
            const info = getInfoForCurrentElement(existingElement.id)
            if (info) {
              const { currentRowId, colIdx, colChildLength } = info
              const foundPage = store.pages?.find(x => x.id === props.sectionId)
              const sectionChildren = foundPage?.component?.options[sectionType.value]?.children ?? []
              const currentTopLevelIdx = sectionChildren.findIndex(c => c?.id === existingElement.id)
              // Stacked blocks (not inside a ROW): getInfoForCurrentElement only walks ROW→columns, so it returns defaults.
              // Must adjust row index after delete like PageRenderer does, or children[rowIndex] is undefined and we push undefined into the tree.
              const inNestedRow = Boolean(currentRowId)
              const rowAdjustOldIdx = inNestedRow ? -1 : currentTopLevelIdx
              const rowAdjustLength = inNestedRow ? colChildLength : sectionChildren.length

              const adjRow = getAdjustedRowIndex(rowAdjustOldIdx, option.rowIndex as number, rowAdjustLength)
              const adjCol = getAdjustedColIndex(
                colIdx,
                option.colIndex as number,
                currentRowId,
                option.rowId as string,
                inNestedRow ? colChildLength : sectionChildren.length
              )

              // Delete the element first to avoid duplication
              store.deleteElementInSection(existingElement?.id, sectionType.value)

              // Add it back at the new position
              store.addElementToSection(el, option.currentPageId as string, adjRow, adjCol, option.rowColIndex as number, sectionType.value)
            } else {
              // If we can't find the element's position info, still try to move it
              // Delete first to avoid duplication
              store.deleteElementInSection(existingElement?.id, sectionType.value)
              // Add at the new position
              store.addElementToSection(
                el,
                option.currentPageId as string,
                option.rowIndex as number,
                option.colIndex as number,
                option.rowColIndex as number,
                sectionType.value
              )
            }
          }
        } else {
          // Element is not in a section, check if it's in main page content
          const pageIdInMainContent = store.getPageIdByElementId(existingElement?.id)
          if (pageIdInMainContent) {
            // Validate: Product list cannot be moved to header/footer
            if (existingElement?.type === ELEMENTS_LOOKUP.PRODUCT_LIST) {
              handleErrorMessage(t('validations.invalid.productListCannotBeAddedToHeaderFooter'))
              return
            }
            // Element is in main page content, move it to the section
            const el = cloneDeep(existingElement)
            if (el) {
              // Delete from main content
              store.deleteElement(existingElement?.id)
              // Add to section
              store.addElementToSection(
                el,
                option.currentPageId as string,
                option.rowIndex as number,
                option.colIndex as number,
                option.rowColIndex as number,
                sectionType.value
              )
            }
          } else {
            // Element might be in a different section, try to move within current section
            const currentIdx = store.pages.find(x => x.id === props.sectionId)?.children.findIndex(x => x.id === existingElement.id)
            if (currentIdx !== undefined && currentIdx !== option.rowIndex) {
              moveElement(option.rowIndex as number, currentIdx as number, existingElement)
            }
          }
        }
      }
    }
  }

  function onDrop(evt: any, idx: number, colIndex: number, rowColIndex: number) {
    // Only allow dropping when section is activated (for header/footer sections)
    if (isHeaderFooterSection.value && !isSectionActivated.value) {
      return
    }

    const containerReact = containerRefSection?.value?.getBoundingClientRect()
    if (!containerReact) {
      console.error('no containerRect found.', containerReact)
      return
    }
    // Calculate position relative to the section's top-left corner
    // containerRefSection is the section container, so containerReact gives us the section's bounding rect
    // evt.clientX and evt.clientY are viewport coordinates
    // Subtracting containerReact.left/top gives position relative to section (0,0 at section top-left)
    const left = evt.clientX - containerReact.left
    const top = evt.clientY - containerReact.top
    const bottom = containerReact.height - top

    const type = evt.dataTransfer.getData('name')

    if (!props.sectionId) return

    // Validate: Product list cannot be added to header/footer
    if (type === ELEMENTS_LOOKUP.PRODUCT_LIST) {
      handleErrorMessage(t('validations.invalid.productListCannotBeAddedToHeaderFooter'))
      return
    }

    if (type === ELEMENTS_LOOKUP.PAGE_BREAK && (colIndex > 0 || rowColIndex > 0)) {
      return
    }
    const el: IElement = createElement({ type }) as IElement
    if (el) {
      store.addElementToSection(
        {
          ...el,
          responsiveStyles: {
            ...el.responsiveStyles,
            large: {
              ...el.responsiveStyles.large,
              scale: { scaleX: 1, scaleY: 1 },
              position: { top, left, bottom, right: 0 },
            },
          },
        } as IElement<any>,
        props.sectionId,
        idx as number,
        colIndex as number,
        rowColIndex as number,
        sectionType.value
      )

      // If a floating element is dropped onto a block in this section, re-parent
      // it under that block and rewrite its `position` so top/left/right/bottom
      // are block-relative (same behavior as dragging an already-placed
      // floating element onto a block). Scope the lookup to THIS section's DOM
      // so a drop in the header can never accidentally match a block in the
      // main content area.
      const dropTarget = resolveDropTargetBlock(evt.clientX, evt.clientY, el, containerRefSection.value)
      if (dropTarget) {
        store.moveElementById(el.id, dropTarget.blockId, sectionType.value)
        store.updateSectionElementStyle({
          id: el.id,
          key: 'position',
          value: dropTarget.position,
          section: sectionType.value,
        })
      }
    }
  }

  function getAdjustedRowIndex(oldIdx: number, newIdx: number, length: number) {
    if (oldIdx < 0) return newIdx
    if (oldIdx === 0) return newIdx - 1
    if (oldIdx < newIdx && length === 1) {
      return newIdx - 1 > -1 ? newIdx - 1 : 0
    }
    if (oldIdx < newIdx) {
      return newIdx - 1
    }
    return newIdx
  }

  function getAdjustedColIndex(oldIdx: number, newIdx: number, currentRowId: string, newRowId: string, length: number) {
    if (currentRowId === newRowId && oldIdx < newIdx && length === 1) {
      return newIdx - 1 > -1 ? newIdx - 1 : 0
    }
    if (oldIdx < 0) return newIdx

    return newIdx
  }

  function getInfoForCurrentElement(id: string) {
    const foundPage = store.pages?.find(x => x.id === props.sectionId)
    if (!foundPage) {
      // Handle the case where no page is found
      console.error('Page not found')
      return
    }

    const rows = foundPage.component.options[sectionType.value]?.children?.filter((x: { type: string }) => x.type === ELEMENTS_LOOKUP.ROW)
    if (!rows) {
      console.error('No rows found')
      return
    }

    return rows.reduce(
      (acc: { colIdx: any; currentRowId: any; colChildLength: any }, row: { children: any[]; id: any }) => {
        if (!row.children) return acc

        row.children.forEach((col, index) => {
          if (!col.children) return acc

          col.children.forEach((el: { id: string }) => {
            if (el.id === id) {
              acc.colIdx = index
              acc.currentRowId = row.id
              acc.colChildLength = col.children.length
            }
          })
        })

        return acc
      },
      { currentRowId: '', colIdx: -1, colChildLength: 0 }
    )
  }
</script>

<template>
  <div
    ref="containerRefSection"
    class="page-section"
    :id="sectionId"
    :class="{
      [snapContainerClass]: true,
      [boundContainerClass]: true,
      [boundContainerClass + `-${sectionType}`]: true,
    }"
    :style="{
      marginTop: '0px',
      marginLeft: '0px',
      marginRight: '0px',
      marginBottom: '0px',
      position: isHeaderFooterSection ? 'relative' : undefined,
    }"
    :data-id="sectionId"
    :data-type="ELEMENTS_LOOKUP.PAGE"
    :data-section-type="sectionType"
  >
    <RenderFloatingComponents
      v-if="elements"
      :parent-id="sectionId"
      :page-id="parentPageId"
      :page-index="pageIndex"
      :child-components-with-draggable="childElementsWithDraggable"
      :snap-container-class="snapContainerClass + `-${sectionType}`"
      :bound-container-class="boundContainerClass + `-${sectionType}`"
      :key="`page-level-key-${sectionId}`"
    />

    <div class="relative draggable-container clearfix flex flex-col" style="width: 100%">
      <div v-if="shouldShowDragText" class="absolute inset-0 flex items-center justify-center pointer-events-none z-0 select-none">
        <span class="text-gray-400 text-sm select-none">
          {{ t('common.dragElementsIntoSection') }}
        </span>
      </div>
      <div
        v-if="actionBarStore.draggingFloatingElement && isSectionActivated"
        class="absolute w-full h-full top-0 left-0 z-[100]"
        @dragover="
          event => {
            event.preventDefault()
          }
        "
        @drop="
          event =>
            onDropElement(event, {
              dir: 'bottom',
              rowIndex: elements.length,
            })
        "
      />
      <ElementDropContainer
        v-if="childComponentsWithoutDraggable.length === 0 && isSectionActivated"
        :for-initial="true"
        :is-header-footer="isHeaderFooterSection"
        @onDropElement="onDropElement"
        width="100%"
        :page-id="sectionId"
        :col-index="0"
        :row-index="0"
        :snap-container-class="snapContainerClass"
        :main-index="0"
      />
      <BlockElementsRenderer
        v-else
        :key="`BlockElementsRenderer-page-level-key-${sectionId}`"
        :elements="childComponentsWithoutDraggable"
        @on-move-start="onStart"
        @on-move-end="onEnd"
        @click="(event: MouseEvent, element: IElement | null) => updateActiveElement(event, element)"
        @mouseOver="mouseEnter"
        @mouseLeave="mouseLeave"
        @focusin="(e: FocusEvent) => mouseEnter(e as unknown as MouseEvent)"
        @focusout="(e: FocusEvent) => mouseLeave(e as unknown as MouseEvent)"
        @on-drop-element="onDropElement"
        :page-id="sectionId"
        :drag-enabled="
          isSectionActivated &&
          (store.moveElementDragState || actionBarStore.dragStateIsActivated) &&
          !actionBarStore.draggingFloatingElement
        "
        :parent-id="sectionId"
        :page-index="pageIndex"
        :snap-container-class="snapContainerClass"
        :bound-container-class="boundContainerClass"
      />
      <span
        v-if="
          isSectionActivated &&
          childComponentsWithoutDraggable.length > 0 &&
          (store.moveElementDragState || actionBarStore.dragStateIsActivated) &&
          !actionBarStore.draggingFloatingElement
        "
        @dragover="(event: DragEvent) => event.preventDefault()"
        @drop="
          (ev: DragEvent) =>
            onDropElement(ev, { dir: 'bottom', rowIndex: childComponentsWithoutDraggable.length, currentPageId: sectionId })
        "
        class="relative block bg-transparent bottom-0 left-0 w-full z-0 flex-1"
      />
    </div>
  </div>
</template>

<style scoped>
  .page-section {
    position: relative;
    &::before {
      position: absolute;
      content: '';
      z-index: 0;
      width: 100%;
      height: 100%;
      will-change: transform;
      transform: translateZ(0px);
      top: 0;
      background-color: v-bind('pageStyles.backgroundColor');
      background-image: v-bind('pageStyles.backgroundImage');
      background-position: v-bind('pageStyles.backgroundPosition');
      background-repeat: v-bind('pageStyles.backgroundRepeat');
      background-size: v-bind('pageStyles.backgroundSize');
      opacity: v-bind('pageStyles.opacity');
    }
  }
</style>
